import * as React from 'react'
import { Link } from 'react-router-dom'

import { InfiniteScroll } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import { debounce } from 'lodash'
import range from 'lodash/range'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import usePagination from 'modules/core/hooks/usePagination'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'

import type Store from '../Store'
import DayCell from './DayCell'
import styles from './index.module.scss'

const cx = classNames.bind(styles)

const canBeSupervisedByCurrent = (employee: Employee) => {
  return employee.isSupervisedByCurrent()
}

const onEmployeeClick = (event: React.MouseEvent, employee: Employee) => {
  if (!canBeSupervisedByCurrent(employee) && !employeeUser(employee).isCurrent()) {
    event.preventDefault()
  }
}

interface TableBodyRowProps {
  store: Store
  employee: Employee
  employeeWorkingDays: string
}

const TableBodyRow = observer(function TableBodyRow({
  store,
  employee,
  employeeWorkingDays,
}: TableBodyRowProps) {
  const { navigation } = useNavigation()

  const { canManageTimeoff } = useCurrentSession()
  const canClick =
    canBeSupervisedByCurrent(employee) || employeeUser(employee).isCurrent() || canManageTimeoff

  const className = cx('link', {
    canClick,
  })

  return (
    <tr>
      <th className={styles.employeeCell}>
        <Link
          onClick={(event) => onEmployeeClick(event, employee)}
          to={navigation.employees.id.fullPath({ id: employee.attributes.id })}
          className={className}
        >
          <div className={styles.avatar}>
            <UserAvatar user={employeeUser(employee)} size="s32" />
          </div>
          <div className={styles.userName}>
            <span>{employeeUser(employee).fullName()}</span>
          </div>
        </Link>
      </th>
      {range(store.daysInMonth).map((day) => {
        const key = `${employee.attributes.id}-${day}`

        return (
          <DayCell
            employee={employee}
            store={store}
            day={day}
            key={key}
            employeeWorkingDays={employeeWorkingDays}
          />
        )
      })}
    </tr>
  )
})
interface Props {
  store: Store
  employees: Employee[]
  scrollWrapper: HTMLElement | null
  employeesWorkingDays: Record<string, string>
}

const TableBody = observer(function TableBody({
  store,
  employees,
  scrollWrapper,
  employeesWorkingDays,
}: Props) {
  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: 20,
    items: employees,
  })

  const scrollListener = debounce(() => {
    if (hasMore) {
      loadMore(page + 1)
    }
  }, 300)

  return (
    <InfiniteScroll
      element="tbody"
      pageStart={1}
      loadMore={scrollListener}
      hasMore={hasMore}
      getScrollParent={() => scrollWrapper}
      useWindow={false}
      loader={
        <tr key={`infiniteLoader-${page}`}>
          <td>
            <Loading />
          </td>
        </tr>
      }
    >
      {paginatedItems.map((employee) => (
        <TableBodyRow
          store={store}
          employee={employee}
          key={employee.attributes.id}
          employeeWorkingDays={employeesWorkingDays[String(employee.attributes.id)] ?? ''}
        />
      ))}
    </InfiniteScroll>
  )
})

export default TableBody
